import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, FileSpreadsheet, Loader2 } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import FilterToolbar, { FilterForm } from './FilterToolbar';

const EMPTY_FILTERS = {
  person: '',
  element: '',
  classification: '',
  q: '',
};

export default function ElementEntriesTable({ refreshKey = 0 }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const hasActiveFilters = Object.values(applied).some((v) => String(v || '').trim());

  const loadRows = useCallback(async (activeFilters) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (activeFilters.person) params.set('person', activeFilters.person);
      if (activeFilters.element) params.set('element', activeFilters.element);
      if (activeFilters.classification) params.set('classification', activeFilters.classification);
      if (activeFilters.q) params.set('q', activeFilters.q);
      params.set('limit', '1000');
      const qs = params.toString();
      const payload = await apiFetch(`/api/reports/element-entries/rows?${qs}`);
      setData(payload);
    } catch (e) {
      setError(e.message || 'Failed to load rows');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows(applied);
  }, [loadRows, applied, refreshKey]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const applyFilters = () => setApplied({ ...filters });

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const downloadExcel = async () => {
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams();
      if (applied.person) params.set('person', applied.person);
      if (applied.element) params.set('element', applied.element);
      if (applied.classification) params.set('classification', applied.classification);
      if (applied.q) params.set('q', applied.q);
      const base = getApiBase();
      const res = await fetch(`${base}/api/reports/element-entries/export?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Export failed. Try again.');
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="([^"]+)"/i.exec(cd);
      const filename = match?.[1] || 'element-entries.xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!data && !loading && !error) return null;
  if (data && data.total === 0 && !hasActiveFilters && !loading) return null;

  const columns = data?.columns || [];
  const elements = data?.filterOptions?.elements || [];
  const elementListId = 'element-entries-element-list';

  const summaryText =
    data?.total != null
      ? `${data.total.toLocaleString()} row(s)${hasActiveFilters ? ' matching filters' : ''}${
          data.total > data.rows?.length ? ` · showing first ${data.rows.length.toLocaleString()}` : ''
        }`
      : 'Loading…';

  return (
    <div className="mt-4">
      <div className="card-panel overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            aria-expanded={expanded}
          >
            <span className="text-sm font-semibold text-slate-200">Element entries results</span>
            <span className="truncate text-xs text-slate-500">{summaryText}</span>
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn-secondary shrink-0 !p-1.5"
            title={expanded ? 'Minimize results' : 'Expand results'}
            aria-label={expanded ? 'Minimize results' : 'Expand results'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {error && !expanded && <p className="alert-error mx-3 mb-2">{error}</p>}

        {expanded && (
          <FilterForm onApply={applyFilters} disabled={loading} className="space-y-3 border-t border-slate-700/80 px-3 pb-3 pt-3">
            <FilterToolbar
              onClear={clearFilters}
              hasActiveFilters={hasActiveFilters}
              disabled={loading}
              extra={
                <button
                  type="button"
                  onClick={downloadExcel}
                  disabled={exporting || !data?.total}
                  className="btn-secondary py-1.5 text-sm"
                  title={
                    data?.total
                      ? `Download ${data.total.toLocaleString()} row(s) to Excel${hasActiveFilters ? ' (filtered)' : ''}`
                      : 'No rows to export'
                  }
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4" />
                  )}
                  Excel
                </button>
              }
            >
              <input
                value={filters.q}
                onChange={(e) => setFilter('q', e.target.value)}
                placeholder="Search all columns…"
                className="filter-input min-w-[12rem] text-sm"
              />
            </FilterToolbar>

            {exportError && <p className="text-xs text-rose-400">{exportError}</p>}

            {error && <p className="alert-error">{error}</p>}

            <div className="overflow-hidden rounded-lg border border-slate-700/80">
              <div className="max-h-[min(480px,55vh)] overflow-auto">
                <table className="data-table w-full min-w-[960px] text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className={`px-2 py-2 font-medium ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="px-2 py-1.5">
                        <input
                          value={filters.person}
                          onChange={(e) => setFilter('person', e.target.value)}
                          placeholder="Person #"
                          className="filter-input"
                        />
                      </th>
                      <th className="px-2 py-1.5" />
                      <th className="px-2 py-1.5">
                        <input
                          list={elements.length ? elementListId : undefined}
                          value={filters.element}
                          onChange={(e) => setFilter('element', e.target.value)}
                          placeholder="Element name…"
                          className="filter-input min-w-[10rem]"
                        />
                        {elements.length > 0 && (
                          <datalist id={elementListId}>
                            {elements.map((name) => (
                              <option key={name} value={name} />
                            ))}
                          </datalist>
                        )}
                      </th>
                      <th className="px-2 py-1.5" colSpan={2} />
                      <th className="px-2 py-1.5">
                        <input
                          value={filters.classification}
                          onChange={(e) => setFilter('classification', e.target.value)}
                          placeholder="Classification…"
                          className="filter-input"
                        />
                      </th>
                      <th className="px-2 py-1.5" colSpan={2} />
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !data?.rows?.length ? (
                      <tr>
                        <td colSpan={columns.length || 1} className="px-3 py-8 text-center text-slate-500">
                          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                        </td>
                      </tr>
                    ) : data?.rows?.length ? (
                      data.rows.map((row, idx) => (
                        <tr key={`${row.PERSON_NUMBER}-${row.ELEMENT_NAME}-${idx}`}>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={`px-2 py-1.5 ${col.align === 'right' ? 'text-right' : ''} ${col.mono ? 'font-mono tabular-nums text-slate-300' : 'text-slate-200'}`}
                            >
                              {row[col.key] ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={columns.length || 1} className="px-3 py-6 text-center text-slate-500">
                          No rows match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </FilterForm>
        )}
      </div>
    </div>
  );
}
